import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  Flex,
  Icon,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
  Button,
  VStack,
  useColorMode,
  HStack,
  Divider,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useClipboard,
  Tooltip,
  IconButton,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  FaShoppingBag,
  FaHotel,
  FaCar,
  FaUtensils,
  FaPlane,
  FaTshirt,
  FaGift,
  FaGlassCheers,
  FaTools,
  FaMapMarkerAlt,
  FaCamera,
  FaShareAlt,
} from "react-icons/fa";
import { useHistory, useParams } from "react-router-dom";
import { FaWallet } from "react-icons/fa";
import { SwatchesPicker, BlockPicker } from "react-color";
import { useCurrency } from "../CurrencyExchange/CurrencyContext";

import axiosInstance from "../../helpers/axiosInstance";
import ExpensesList from "./ExpensesList";
import CategoryList from "./CategoryList";
import BudgetMenu from "./BudgetMenu";
import ExpenseChart from "./ExpenseChart";
import ExpenseModal from "./ExpenseModal";
import TripsList from "./TripsList";
import usePersistedState from "../usePersistedState";
import Translate from "../../components/LanguageTranslation/Translate";
import { Helmet } from "react-helmet-async";

import Loading from "../common/Loading";

export const categoryIcons = {
  Places: { icon: FaMapMarkerAlt },
  Accommodations: { icon: FaHotel },
  Transportation: { icon: FaCar },
  Food: { icon: FaUtensils },
  Shopping: { icon: FaShoppingBag },
  Entertainment: { icon: FaGlassCheers },
  Gifts: { icon: FaGift },
  Clothing: { icon: FaTshirt },
  Photography: { icon: FaCamera },
  Tools: { icon: FaTools },
  Flights: { icon: FaPlane },
};

export const textColor = (color) => {
  if (!color) return "black";
  const hex = color.replace(/^#/, "");

  const bigint = parseInt(hex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.5 ? "black" : "white";
};

const BudgetPlanner = () => {
  const { tripId } = useParams();
  const [trips, setTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = usePersistedState(
    "selectedTripBudgetPlanner",
    ""
  );
  const [categories, setCategories] = useState([]);
  const [tripName, setTripName] = usePersistedState(
    "tripNameBudgetPlanner",
    ""
  );
  const [expenses, setExpenses] = usePersistedState("expenses", []);
  // Manually fix corrupted localStorage value
  const corrupted = localStorage.getItem("totalBudgetBudgetPlanner");
  if (corrupted === "undefined") {
    localStorage.removeItem("totalBudgetBudgetPlanner");
  }

  const [totalBudget, setTotalBudget] = usePersistedState(
    "totalBudgetBudgetPlanner",
    0
  );

  const [view, setView] = useState("stats"); // Track view: "stats", "expenses", "categories"

  const [newCost, setNewCost] = useState("");
  const [newExpenseName, setNewExpenseName] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedColor, setSelectedColor] = useState("#ffd700");
  const [selectedIcon, setSelectedIcon] = useState("Shopping");
  const [selectedExpenseColor, setSelectedExpenseColor] = useState("#ffd700");
  const { currency } = useCurrency();
  const [isShared, setIsShared] = useState("personal");
  const [editingExpenseIndex, setEditingExpenseIndex] = useState(null);
  const [oldExpenseName, setOldExpenseName] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdatingExpense, setIsUpdatingExpense] = useState(false);
  const { colorMode } = useColorMode();
  const history = useHistory();
  const toast = useToast();

  const redirectToChat = () => {
    history.push("/ai-travel-planner/chat"); // Replace '/chat' with the actual path to your chat page
  };

  const redirectToBucketList = () => {
    history.push("/ai-travel-planner/bucket-list"); // Replace '/chat' with the actual path to your chat page
  };

  // Separate modal state for ExpensesList
  const {
    isOpen: isExpenseModalOpen,
    onOpen: onOpenExpenseModal,
    onClose: onCloseExpenseModal,
  } = useDisclosure();

  // Separate modal state for CategoryList
  const {
    isOpen: isCategoryModalOpen,
    onOpen: onOpenCategoryModal,
    onClose: onCloseCategoryModal,
  } = useDisclosure();

  const [newCategoryName, setNewCategoryName] = useState("");

  const { currency: userCurrency } = useCurrency();
  const [selectedCurrency, setSelectedCurrency] = useState({
    value: userCurrency,
    label: `${userCurrency}`,
  });

  // Share modal state
  const [isShareOpen, setIsShareOpen] = useState(false);
  const onShareOpen = () => setIsShareOpen(true);
  const onShareClose = () => setIsShareOpen(false);
  const shareUrl = `${window.location.origin}/ai-travel-planner/budget-planner/${selectedTrip}`;
  const { hasCopied, onCopy } = useClipboard(shareUrl);

  useEffect(() => {
    const fetchTrips = async () => {
      setIsLoading(true);
      try {
        const response = await axiosInstance.get("/bucket-list/budget/budget_planner");
        setTrips(response.data.data); // Set trips from the API response
      } catch (error) {
        console.error("Error fetching trips:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTrips();
  }, [selectedTrip]);

  useEffect(() => {
    if (tripId && tripId !== "") {
      setSelectedTrip(tripId);
    }
  }, [tripId]);

  // Function to add a new category
  const addCategory = async () => {
    try {
      if (!newCategoryName) {
        toast({
          title: "Empty category",
          description: "Please fill in a name for the category.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      await axiosInstance.post(
        `/budget_planner/${selectedTrip}/create_category`,
        {
          name: newCategoryName,
          color: selectedColor,
          icon: selectedIcon,
        }
      );
      setNewCategoryName(""); // Clear input field after adding the category
      onCloseCategoryModal(); // Close the modal after adding
      // Re-fetch categories to update the list
      const response = await axiosInstance.get(
        `/budget_planner/${selectedTrip}/categories`
      );
      setCategories(response.data);
    } catch (error) {
      // console.log(error.response.data);
      let description = "Something went wrong when adding category.";
      if (error.response.data.message === "Category already exists") {
        description = "A category with this name already exists.";
      }
      toast({
        title: "Error",
        description: description,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      console.error("Error adding category:", error);
    }
  };

  const addExpense = async () => {
    if (!newExpenseName.trim() || !selectedCategory) {
      toast({
        title: "Empty fields",
        description: "Please fill in the name and category.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    let newCostFloat = parseFloat(newCost);
    if (!newCostFloat || newCostFloat <= 0) {
      toast({
        title: "Invalid cost",
        description: "Please enter a valid cost.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    
    if (editingExpenseIndex !== null) {
      // Update existing expense
      setIsUpdatingExpense(true);
      try {
        const response = await axiosInstance.put(
          `/budget_planner/${selectedTrip}/update_expense`,
          {
            category: selectedCategory,
            expense_name: oldExpenseName,
            new_expense_name: newExpenseName,
            expense_currency: selectedCurrency,
            new_cost: newCostFloat,
            expense_date: selectedDate,
            expense_location: selectedLocation,
            expense_color: selectedExpenseColor,
            expense_user: isShared,
          }
        );
        setEditingExpenseIndex(null);
        
        // Show success toast with backend response
        toast({
          title: "Success",
          description: response.data.message || "Expense updated successfully!",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        console.error("Error updating expense:", error);
        
        // Show error toast with backend response
        const errorMessage = error.response?.data?.message || "Failed to update expense. Please try again.";
        toast({
          title: "Error",
          description: errorMessage,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsUpdatingExpense(false);
      }
    } else {
      // Add new expense
      try {
        await axiosInstance.post(
          `/budget_planner/${selectedTrip}/add_expense`,
          {
            category: selectedCategory,
            expense_name: newExpenseName,
            expense_currency: selectedCurrency,
            cost: parseFloat(newCost),
            expense_date: selectedDate,
            expense_location: selectedLocation,
            expense_color: selectedExpenseColor,
            expense_user: isShared,
          }
        );
      } catch (error) {
        console.error("Error adding expense:", error);
      }
    }

    // Re-fetch expenses to update the list
    const response = await axiosInstance.get(
      `/budget_planner/${selectedTrip}/expenses`
    );
    setExpenses(response.data);

    setNewExpenseName("");
    setNewCost("");
    setSelectedCategory(null);
    onCloseExpenseModal();
  };

  const resetForm = () => {
    setNewExpenseName("");
    setNewCost("");
    setSelectedCategory(null);
    setEditingExpenseIndex(null);
    setOldExpenseName("");
    onCloseExpenseModal();
  };

  // When the modal opens, set selectedCurrency to the user's preferred currency if not already set or if currency changes
  useEffect(() => {
    setSelectedCurrency((prev) => {
      // If the user has already selected a currency, don't override
      if (prev && prev.value) return prev;
      return { value: currency, label: `${currency}` };
    });
  }, [currency, isExpenseModalOpen]);

  // When the modal opens for a new expense, set selectedCurrency to the user's preferred currency
  useEffect(() => {
    if (isExpenseModalOpen && editingExpenseIndex === null) {
      setSelectedCurrency({ value: currency, label: `${currency}` });
    }
  }, [isExpenseModalOpen, currency, editingExpenseIndex]);

  if (isLoading) {
    return <Loading fullScreen />;
  }

  return (
    <>
      <Helmet>
        <title>AI Budget Planner&nbsp;| NOMAD360</title>
      </Helmet>

      <Box
        display="flex"
        bg="white"
        w="100%"
        h={{ base: "calc(100vh - 64px)", md: "100vh", lg: "100vh" }}
        overflowY="hidden"
      >
        {trips.length === 0 ? (
          <Flex
            w="100%"
            h={{ base: "calc(100vh - 64px)", md: "100vh", lg: "100vh" }}
            justifyContent="center"
            alignItems="center"
            direction="column"
          >
            <Box textAlign="center" mb={4}>
              <Icon
                as={FaWallet}
                boxSize={12}
                color={colorMode === "dark" ? "#1A202C" : "#1A202C"}
              />
              <Text
                fontSize="2xl"
                fontWeight="bold"
                mt={2}
                color={colorMode === "dark" ? "#1A202C" : "#1A202C"}
                mb={0}
              >
                <Translate text="Your Budget Planner is Empty" />
              </Text>
              <Text
                fontSize="md"
                mt={2}
                color={colorMode === "dark" ? "#1A202C" : "#1A202C"}
                mb={0}
              >
                <Translate text="You don't have any trips planned at the moment." />
              </Text>
              <Text
                fontSize="md"
                mt={2}
                color={colorMode === "dark" ? "#1A202C" : "#1A202C"}
                mb={0}
              >
                <Translate text="Start planning your next adventure or enable your saved trip!" />
              </Text>
              <HStack alignContent="center" justifyContent="center">
                <Button
                  mt={6}
                  bg="#286328"
                  color="white"
                  _hover="none"
                  borderRadius="lg"
                  onClick={redirectToChat}
                >
                  <Translate text="Plan a trip" />
                </Button>
                <Divider
                  orientation="vertical"
                  h="40px"
                  borderColor={colorMode === "dark" ? "black" : "black"}
                  mt={6}
                  mb={0}
                />
                <Button
                  mt={6}
                  bg="#286328"
                  color="white"
                  _hover="none"
                  borderRadius="lg"
                  onClick={redirectToBucketList}
                >
                  <Translate text="Bucket list" />
                </Button>
              </HStack>
            </Box>
          </Flex>
        ) : (
          <VStack
            display={{ base: "block", md: "block" }}
            w="100%"
            h={{ base: "calc(100vh - 64px)", md: "100vh", lg: "100vh" }}
          >
            {/* <HStack
              display={{ base: "flex", md: "none" }}
              p={2}
              pl={4}
              justifyContent="space-between"
              alignItems="center"
              mb={4}
              borderBottom="1px"
              borderColor="blackAlpha.100"
              height="8vh"
            >
              <Text
                fontSize="lg"
                fontWeight="bold"
                color="blackAlpha.800"
                mb={0}
              >
                <Translate text="Budget Planner" />
              </Text>
              <SettingsMenu />
            </HStack> */}

            <Flex
              w="100%"
              h={{ base: "calc(92vh - 64px)", md: "100vh", lg: "100vh" }}
              direction={{ base: "column", md: "row", lg: "row" }}
              overflowY={{ base: "auto", md: "hidden", lg: "hidden" }}
            >
              <BudgetMenu
                selectedTrip={selectedTrip}
                setSelectedTrip={setSelectedTrip}
                tripName={tripName}
                setTripName={setTripName}
                setView={setView} // Pass setView to switch between views
                view={view} // Pass the current view to BudgetMenu
                onOpen={onOpenExpenseModal} // Trigger the expense modal when "Add Expense" is clicked
                onOpenCategoryModal={onOpenCategoryModal}
                expenses={expenses}
                totalBudget={totalBudget}
                onShareOpen={onShareOpen}
              />

              {/* Render ExpenseChart, ExpensesList, and CategoryList only if a trip is selected */}
              {!selectedTrip && trips && (
                <TripsList trips={trips} setSelectedTrip={setSelectedTrip} />
              )}
              {selectedTrip && (
                <>
                  {view === "stats" && (
                    <ExpenseChart
                      total={3748}
                      expenses={expenses}
                      setExpenses={setExpenses}
                      categories={categories}
                      setCategories={setCategories}
                      selectedTripId={selectedTrip}
                      totalBudget={totalBudget}
                      setTotalBudget={setTotalBudget}
                    />
                  )}

                  {view === "expenses" && (
                    <ExpensesList
                      expenses={expenses}
                      setExpenses={setExpenses}
                      categories={categories}
                      selectedTripId={selectedTrip}
                      isOpen={isExpenseModalOpen}
                      onOpen={onOpenExpenseModal}
                      onClose={onCloseExpenseModal}
                      addExpense={addExpense}
                      newExpenseName={newExpenseName}
                      setNewExpenseName={setNewExpenseName}
                      newCost={newCost}
                      setNewCost={setNewCost}
                      setSelectedCurrency={setSelectedCurrency}
                      selectedCategory={selectedCategory}
                      setSelectedCategory={setSelectedCategory}
                      selectedDate={selectedDate}
                      setSelectedDate={setSelectedDate}
                      selectedLocation={selectedLocation}
                      setSelectedLocation={setSelectedLocation}
                      setEditingExpenseIndex={setEditingExpenseIndex}
                      setSelectedColor={setSelectedExpenseColor}
                      setIsShared={setIsShared}
                      selectedTrip={selectedTrip}
                      totalBudget={totalBudget}
                      setTotalBudget={setTotalBudget}
                      setOldExpenseName={setOldExpenseName}
                    />
                  )}

                  {view === "categories" && (
                    <CategoryList
                      categories={categories}
                      setCategories={setCategories}
                      selectedTripId={selectedTrip}
                      selectedTrip={selectedTrip}
                      onOpenCategoryModal={onOpenCategoryModal}
                    />
                  )}
                </>
              )}

              <ExpenseModal
                isOpen={isExpenseModalOpen}
                onClose={resetForm}
                addExpense={addExpense}
                categories={categories}
                newExpenseName={newExpenseName}
                setNewExpenseName={setNewExpenseName}
                newCost={newCost}
                isShared={isShared}
                setNewCost={setNewCost}
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                selectedLocation={selectedLocation}
                setSelectedLocation={setSelectedLocation}
                selectedCurrency={selectedCurrency}
                setSelectedCurrency={setSelectedCurrency}
                selectedColor={selectedExpenseColor}
                setSelectedColor={setSelectedExpenseColor}
                setIsShared={setIsShared}
                isUpdatingExpense={isUpdatingExpense}
                modalHeader={
                  editingExpenseIndex !== null
                    ? "Edit expense"
                    : "Create new expense"
                }
              />
              {/* Modal for adding a new category */}
              <Modal
                isOpen={isCategoryModalOpen}
                onClose={onCloseCategoryModal}
                size={{ base: "xs", md: "lg" }}
                isCentered
                scrollBehavior="inside"
              >
                <ModalOverlay />
                <ModalContent p={4} borderRadius="lg">
                  <ModalHeader textAlign="center">
                    <Translate text="Add New Category" />
                  </ModalHeader>
                  <ModalCloseButton />
                  <ModalBody>
                    <VStack spacing={4} align="stretch" w="100%">
                      <Input
                        placeholder="Enter new category name"
                        bg="white"
                        borderRadius="md"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        w="full"
                      />

                      <Flex
                        flexDirection={{ base: "column", md: "row" }} // Stack items vertically on mobile
                        gap={8}
                        alignItems="center"
                        justify="center" // Center content on mobile
                        w="full"
                      >
                        {/* Category Color Picker */}
                        <Box>
                          <Menu>
                            <MenuButton
                              as={Button}
                              bg={selectedColor}
                              color={textColor(selectedColor)}
                              _hover={{ bg: selectedColor, opacity: 0.8 }}
                              _active={{ bg: selectedColor }}
                              _focus={{
                                bg: selectedColor,
                                boxShadow: "outline",
                              }}
                            >
                              <Translate text="Category Color" />
                            </MenuButton>
                            <MenuList p={2}>
                              <BlockPicker
                                color={selectedColor}
                                colors={[
                                  // Warm colors
                                  "#FF5733",
                                  "#FF8D33",
                                  "#FFBD33",
                                  "#FFD700",
                                  "#DC143C",
                                  "#FF3366",
                                  "#FF33A1",

                                  // Cool colors
                                  "#33FF57",
                                  "#33FF8D",
                                  "#33FFF0",
                                  "#33A1FF",
                                  "#3357FF",
                                  "#5733FF",
                                  "#8D33FF",
                                  "#A133FF",

                                  // Neutral/Natural tones
                                  "#008000",
                                  "#2E8B57",
                                  "#4B0082",
                                  "#808080",
                                  "#A9A9A9",
                                ]}
                                onChange={(color) =>
                                  setSelectedColor(color.hex)
                                }
                              />
                            </MenuList>
                          </Menu>
                        </Box>

                        {/* Icon Picker */}
                        <Flex align="center" justify="center">
                          <Text fontSize="md" fontWeight="bold" mr={2} mb={0}>
                            <Translate text="Icon:" />
                          </Text>
                          <Menu>
                            <MenuButton
                              as={Button}
                              rightIcon={<ChevronDownIcon />}
                            >
                              <Box
                                w="40px"
                                h="40px"
                                bg={selectedColor}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                borderRadius="full"
                              >
                                <Icon
                                  as={categoryIcons[selectedIcon]?.icon}
                                  color="white"
                                  boxSize={5}
                                />
                              </Box>
                            </MenuButton>
                            <MenuList
                              h={{ base: "40vh", md: "50vh" }}
                              overflowY="scroll"
                            >
                              {Object.keys(categoryIcons).map(
                                (category, index) => (
                                  <MenuItem
                                    key={index}
                                    onClick={() => setSelectedIcon(category)}
                                  >
                                    <Box
                                      w="40px"
                                      h="40px"
                                      bg={selectedColor}
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="center"
                                      borderRadius="full"
                                    >
                                      <Icon
                                        as={categoryIcons[category].icon}
                                        color="white"
                                        boxSize={5}
                                      />
                                    </Box>
                                    <Text
                                      fontSize="md"
                                      fontWeight="bold"
                                      ml={2}
                                      mb={0}
                                    >
                                      <Translate text={category} />
                                    </Text>
                                  </MenuItem>
                                )
                              )}
                            </MenuList>
                          </Menu>
                        </Flex>
                      </Flex>
                    </VStack>
                  </ModalBody>
                  <ModalFooter>
                    <Button
                      bg="#286328"
                      color="white"
                      _hover="none"
                      borderRadius="xl"
                      w="full"
                      onClick={addCategory}
                    >
                      <Translate text="Add Category" />
                    </Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>
            </Flex>
            {/* Share Modal */}
            <Modal
              isOpen={isShareOpen}
              onClose={onShareClose}
              size={{ base: "xs", md: "lg" }}
              // isCentered
            >
              <ModalOverlay />
              <ModalContent>
                <ModalHeader>
                  <Translate text="Share Budget Planner" />
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  <Text mb={4}>
                    <Translate text="Anyone with this link can request access." />
                  </Text>
                  <Box
                    bg={colorMode === "light" ? "gray.100" : "gray.700"}
                    p={3}
                    borderRadius="md"
                    wordBreak="break-all"
                  >
                    {shareUrl}
                  </Box>
                </ModalBody>
                <ModalFooter>
                  <Button
                    onClick={onCopy}
                    bg="black"
                    color="white"
                    _hover="none"
                    _active="none"
                    mr={3}
                  >
                    <Translate text={hasCopied ? "Copied" : "Copy Link"} />
                  </Button>
                  <Button variant="ghost" onClick={onShareClose}>
                    <Translate text="Close" />
                  </Button>
                </ModalFooter>
              </ModalContent>
            </Modal>
          </VStack>
        )}
      </Box>
    </>
  );
};

export default BudgetPlanner;
